import { useState, useEffect, useContext } from 'react'
import { Component as ComponentIcon, Link, Shapes } from 'lucide-react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { FieldPicker } from '@components/pickers/field-picker'
import { PatternPicker } from '@components/pickers/pattern-picker'
import { Button } from '@components/v2/button'
import { fieldSetContext } from '@contexts'
import { useAppSelector } from '@redux/hooks'
import { SelectAiClassifications } from './select-ai-classifications'
import { api } from 'api'
import * as TemplateActions from '../template.actions'
import { t } from '@lingui/core/macro'

export const DefineAndGovernActions = () => {
  const [anchor, setAnchor] = useState(null)
  const [picker, setPicker] = useState(null)
  const [patterns, setPatterns] = useState([])
  const [patternsById, setPatternsById] = useState({})
  const { byId, fieldsById } = useContext(fieldSetContext)
  const templateState = useSelector(state => state.Template, shallowEqual)
  const [CREATIVE_CLASSIFICATION_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.CREATIVE_CLASSIFICATION]),
  )

  const dispatch = useDispatch()

  const selected = useSelector(state =>
    Object.values(state.Template.templateFieldsById)
      .filter(tf => tf.field && tf.field.is_pattern)
      .reduce((memo, tf) => {
        memo[tf.field_id] = true
        return memo
      }, {}),
  )

  const hasLinkField = !!useSelector(state =>
    Object.values(state.Template.templateFieldsById).find(
      tf => tf.link_governance || (tf.field && tf.field.governance_type === 'link'),
    ),
  )

  const selectedFields = useSelector(state =>
    Object.values(state.Template.templateFieldsById).reduce((memo, tf) => {
      memo[tf.field_id] = tf.field
      return memo
    }, {}),
  )

  useEffect(() => {
    api.get('/api/v1/account/field/patterns').then(res => {
      setPatterns(res.data)
      setPatternsById(
        res.data.reduce((memo, item) => {
          memo[item.id] = item
          return memo
        }, {}),
      )
    })
  }, [])

  function addPatterns(selectedPatterns) {
    setAnchor(null)
    setPicker(null)
    const fields = selectedPatterns.reduce((memo, pat) => {
      // add the patterns
      memo.push(patternsById[pat])
      // add all their segment fields
      memo = memo.concat(patternsById[pat].segments)
      return memo
    }, [])
    dispatch(TemplateActions.addFields(fields))
    handleClose()
  }

  function addFields(selectedFields) {
    setAnchor(null)
    setAnchor(null)
    const fields = selectedFields.map(field => {
      return fieldsById[field]
    })
    const fieldsets = fields.reduce((memo, field) => {
      byId[field.fieldset_id] && memo.push(byId[field.fieldset_id])
      return memo
    }, [])
    dispatch(TemplateActions.addFieldsets(fieldsets))
    dispatch(TemplateActions.addFields(fields))
    handleClose()
  }

  function handleClick(e, picker) {
    setPicker(picker)
    setAnchor(e.currentTarget)
  }

  function addLinkField() {
    dispatch(TemplateActions.addLinkField())
  }

  function handleClose() {
    setAnchor(null)
    setPicker(null)
  }

  const { template } = templateState
  const { creativeConfiguration, isCreativeClassificationEnabled = false } = template
  const templateFields =
    templateState.templateFields.map(id => templateState.templateFieldsById[id]) || []

  return (
    <>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 15, alignItems: 'center' }}
      >
        {CREATIVE_CLASSIFICATION_ENABLED && (
          <SelectAiClassifications
            hasSourceUrl={templateFields?.some(
              tf => tf.classification_parameter_type === 'SOURCE_URL',
            )}
            isCreativeClassificationEnabled={isCreativeClassificationEnabled}
            creativeConfiguration={creativeConfiguration}
            onChangeClassifications={classifications => {
              dispatch(TemplateActions.setCreativeConfiguration(classifications))
            }}
          />
        )}
        <Button
          variant="outline"
          onClick={e => {
            handleClick(e, 'pattern')
          }}
        >
          <Shapes size={16} />
          {t`Add patterns`}
        </Button>
        <Button
          variant="outline"
          onClick={e => {
            handleClick(e, 'field')
          }}
        >
          <ComponentIcon size={16} />
          {t`Add fields`}
        </Button>
        <Button disabled={Boolean(hasLinkField)} onClick={addLinkField} variant="outline">
          <Link size={16} />
          {t`Add link`}
        </Button>
      </div>
      {picker === 'pattern' && (
        <PatternPicker
          anchorEl={anchor}
          open={!!(anchor && picker === 'pattern')}
          onClose={handleClose}
          patterns={patterns}
          onApplySelection={addPatterns}
          selected={selected}
          style={{ padding: 0, zIndex: 5 }}
          placement="bottom-end"
        />
      )}
      {picker === 'field' && (
        <FieldPicker
          anchorEl={anchor}
          open={!!(anchor && picker === 'field')}
          onClose={handleClose}
          onApplySelection={addFields}
          selected={selectedFields}
          style={{ padding: 0, zIndex: 5 }}
          placement="bottom-end"
        />
      )}
    </>
  )
}
